import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { subDays } from 'date-fns'
import type { DateRange } from '@/modules/shared/types'
import type { CampaignType } from '@/modules/calendar/types'
import {
  DEFAULT_KPI_METRICS,
  DEFAULT_CHART_TABS,
  DEFAULT_CHANNEL_SECTIONS,
  type ShopMetricDefinition,
  type ChartTabDefinition,
  type ChannelSectionDefinition,
} from '@/modules/shop/metric-catalog'
import {
  DEFAULT_VIDEO_KPI_METRICS,
  type VideoMetricDefinition,
} from '@/modules/videos/metric-catalog'
import {
  DEFAULT_ADS_KPI_METRICS,
  type AdsMetricDefinition,
} from '@/modules/ads/metric-catalog'

export interface ShopMetricPrefs {
  kpi: Array<ShopMetricDefinition['key']>
  chart: Array<ChartTabDefinition['key']>
  channel: Array<ChannelSectionDefinition['key']>
}

export interface VideoMetricPrefs {
  kpi: Array<VideoMetricDefinition['key']>
}

export interface AdsMetricPrefs {
  kpi: Array<AdsMetricDefinition['key']>
}

export interface CampaignOverlayState {
  hiddenTypes: CampaignType[]
  hiddenIds: string[]
}

interface AppState {
  sidebarCollapsed: boolean
  dateRange: DateRange
  shopMetricPrefs: ShopMetricPrefs
  videoMetricPrefs: VideoMetricPrefs
  adsMetricPrefs: AdsMetricPrefs
  campaignOverlayPrefs: Record<string, CampaignOverlayState>
  toggleSidebar: () => void
  setDateRange: (range: DateRange) => void
  setShopMetricPrefs: (prefs: Partial<ShopMetricPrefs>) => void
  setVideoMetricPrefs: (prefs: Partial<VideoMetricPrefs>) => void
  setAdsMetricPrefs: (prefs: Partial<AdsMetricPrefs>) => void
  toggleOverlayType: (clientId: string, type: CampaignType) => void
  toggleOverlayCampaign: (clientId: string, id: string) => void
}

const noopStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
}

const defaultShopPrefs: ShopMetricPrefs = {
  kpi: DEFAULT_KPI_METRICS,
  chart: DEFAULT_CHART_TABS,
  channel: DEFAULT_CHANNEL_SECTIONS,
}

const defaultVideoPrefs: VideoMetricPrefs = {
  kpi: DEFAULT_VIDEO_KPI_METRICS,
}

const defaultAdsPrefs: AdsMetricPrefs = {
  kpi: DEFAULT_ADS_KPI_METRICS,
}

function toggleInArray<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
}

const emptyOverlay = (): CampaignOverlayState => ({ hiddenTypes: [], hiddenIds: [] })

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      // Default to the last 7 days (today + 6 prior), matching the
      // 'Last 7 days' preset in the date-range picker. Not persisted
      // (see partialize), so this recomputes on every load.
      dateRange: ((): DateRange => {
        const to = new Date()
        return { from: subDays(to, 6), to }
      })(),
      shopMetricPrefs: defaultShopPrefs,
      videoMetricPrefs: defaultVideoPrefs,
      adsMetricPrefs: defaultAdsPrefs,
      campaignOverlayPrefs: {},
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setDateRange: (dateRange) => set({ dateRange }),
      setShopMetricPrefs: (prefs) =>
        set((s) => ({ shopMetricPrefs: { ...s.shopMetricPrefs, ...prefs } })),
      setVideoMetricPrefs: (prefs) =>
        set((s) => ({ videoMetricPrefs: { ...s.videoMetricPrefs, ...prefs } })),
      setAdsMetricPrefs: (prefs) =>
        set((s) => ({ adsMetricPrefs: { ...s.adsMetricPrefs, ...prefs } })),
      toggleOverlayType: (clientId, type) =>
        set((s) => {
          const prev = s.campaignOverlayPrefs[clientId] ?? emptyOverlay()
          return {
            campaignOverlayPrefs: {
              ...s.campaignOverlayPrefs,
              [clientId]: { ...prev, hiddenTypes: toggleInArray(prev.hiddenTypes, type) },
            },
          }
        }),
      toggleOverlayCampaign: (clientId, id) =>
        set((s) => {
          const prev = s.campaignOverlayPrefs[clientId] ?? emptyOverlay()
          return {
            campaignOverlayPrefs: {
              ...s.campaignOverlayPrefs,
              [clientId]: { ...prev, hiddenIds: toggleInArray(prev.hiddenIds, id) },
            },
          }
        }),
    }),
    {
      name: 'scc-app',
      version: 4,
      migrate: (persisted, version) => {
        const prior = (persisted ?? {}) as Partial<AppState>
        if (version < 2) {
          return {
            ...prior,
            shopMetricPrefs: defaultShopPrefs,
            videoMetricPrefs: defaultVideoPrefs,
            adsMetricPrefs: defaultAdsPrefs,
          } as Partial<AppState>
        }
        if (version < 3) {
          return {
            ...prior,
            videoMetricPrefs: defaultVideoPrefs,
            adsMetricPrefs: defaultAdsPrefs,
          } as Partial<AppState>
        }
        if (version < 4) {
          return {
            ...prior,
            campaignOverlayPrefs: prior.campaignOverlayPrefs ?? {},
          } as Partial<AppState>
        }
        return prior as Partial<AppState>
      },
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        shopMetricPrefs: state.shopMetricPrefs,
        videoMetricPrefs: state.videoMetricPrefs,
        adsMetricPrefs: state.adsMetricPrefs,
        campaignOverlayPrefs: state.campaignOverlayPrefs,
      }),
      merge: (persisted, current) => {
        const p = (persisted as Partial<AppState>) ?? {}
        return {
          ...current,
          sidebarCollapsed: p.sidebarCollapsed ?? current.sidebarCollapsed,
          shopMetricPrefs: {
            kpi: p.shopMetricPrefs?.kpi ?? current.shopMetricPrefs.kpi,
            chart: p.shopMetricPrefs?.chart ?? current.shopMetricPrefs.chart,
            channel: p.shopMetricPrefs?.channel ?? current.shopMetricPrefs.channel,
          },
          videoMetricPrefs: {
            kpi: p.videoMetricPrefs?.kpi ?? current.videoMetricPrefs.kpi,
          },
          adsMetricPrefs: {
            kpi: p.adsMetricPrefs?.kpi ?? current.adsMetricPrefs.kpi,
          },
          campaignOverlayPrefs: p.campaignOverlayPrefs ?? current.campaignOverlayPrefs,
        }
      },
      storage: createJSONStorage(() =>
        typeof window !== 'undefined' ? localStorage : noopStorage,
      ),
    },
  ),
)
